import { db } from "../../main";
import { getClientId, getMemberClientIds } from "@/data/repository/ClientRepository";
import { ComBoxModule } from "@/store/modules/ComBoxesModule";
import { ComBox } from "../models/ComBox";
import { IresDeviceAssignmetType } from "../models/PanelAssignment";
import { observeDevicesOfClients } from "./MemberClientDeviceObserver";

export async function getComBox(serialNumber: string, cacheAllowed?: boolean) {
  const ref = db.collection("comboxes").doc(serialNumber);

  if (cacheAllowed) {
    const cachedPanel = ComBoxModule.cachedComBoxes(serialNumber);

    if (cachedPanel) {
      return cachedPanel;
    }
  }

  const result = (await ref.get()).data() as ComBox;

  ComBoxModule.cacheComBox(result);

  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateComBox(serialNumber: string, update: any) {
  const ref = db.collection("comboxes").doc(serialNumber);

  return ref.set(update, { merge: true });
}

export function observeTestComBoxes(onNext: (comBoxes: Array<ComBox>) => void) {
  const cachedPanels = Array.from(ComBoxModule.comBoxCache.values()).filter(
    (comBox) =>
      comBox.currentAssignment?.assignmentType ===
      IresDeviceAssignmetType.TESTING
  );

  onNext(cachedPanels);

  return db
    .collection("comboxes")
    .where("currentAssignment.assignmentType", "==", "TESTING")
    .onSnapshot((snapshot) => {
      const comBoxes = snapshot.docs.map((doc) => doc.data() as ComBox);

      comBoxes.forEach((comBox) => ComBoxModule.cacheComBox(comBox));

      onNext(comBoxes);
    });
}

export function observeComBoxesAssignedToMembers(
  onNext: (comBoxes: Array<ComBox>) => void
) {
  const memberClientIds = getMemberClientIds();
  const memberClientIdSet = new Set(memberClientIds);

  const cachedComBoxes = Array.from(
    ComBoxModule.comBoxesOfClientCache.values()
  ).filter(
    (comBox) =>
      !!comBox.currentAssignment?.clientId &&
      memberClientIdSet.has(comBox.currentAssignment.clientId)
  );

  return observeDevicesOfClients<ComBox>(
    "comboxes",
    memberClientIds,
    onNext,
    cachedComBoxes
  );
}

export function observeComBoxesAssignedTo(
  clientId: string,
  userId: string | null | undefined,
  onNext: (comBoxes: Array<ComBox>) => void
) {
  const cachedPanels = Array.from(
    ComBoxModule.comBoxesOfClientCache.values()
  ).filter((comBox) => comBox.currentAssignment?.clientId === clientId);

  onNext(cachedPanels);

  if (userId) {
    return db
      .collection("comboxes")
      .where("currentAssignment.clientId", "==", clientId)
      .where(
        "currentAssignment.currentProjectResponsibleUserIds",
        "array-contains-any",
        [userId]
      )
      .onSnapshot((snapshot) => {
        const comBoxesOfClient = snapshot.docs.map(
          (doc) => doc.data() as ComBox
        );

        ComBoxModule.cacheComBoxesOfClient(comBoxesOfClient);
        onNext(comBoxesOfClient);
      });
  } else {
    return db
      .collection("comboxes")
      .where("currentAssignment.clientId", "==", clientId)
      .onSnapshot((snapshot) => {
        const comBoxesOfClient = snapshot.docs.map(
          (doc) => doc.data() as ComBox
        );

        ComBoxModule.cacheComBoxesOfClient(comBoxesOfClient);
        onNext(comBoxesOfClient);
      });
  }
}

export function observeComBoxesOfMemberClients(
  onNext: (panels: Array<ComBox>) => void
) {
  return observeComBoxesAssignedToMembers(onNext);
}

export function observeComBoxesOfClient(
  userId: string | null | undefined,
  onNext: (panels: Array<ComBox>) => void
) {
  return observeComBoxesAssignedTo(getClientId(), userId, onNext);
}

/**
 * Observes one ComBox record.
 *
 * @param onMissing - called when the server confirms there is no record for this serial number.
 *   Never on a cache-only answer, which is empty before the server has replied.
 */
export function observeComBox(
  serialNumber: string,
  onNext: (ComBox: ComBox) => void,
  onMissing?: () => void
) {
  const cachedComBox = ComBoxModule.cachedComBoxes(serialNumber);

  if (cachedComBox) {
    onNext(cachedComBox);
  }

  const ref = db.collection("comboxes").doc(serialNumber);

  return ref.onSnapshot((snapshot) => {
    const comBox = snapshot.data() as ComBox;

    if (comBox) {
      ComBoxModule.cacheComBox(comBox);
      onNext(comBox);
      return;
    }

    if (!snapshot.metadata.fromCache) {
      onMissing?.();
    }
  });
}
